import type { ExplicitType } from "../../utils";

import type { IAttributeTemplate, IScalarAttribute } from "./attributes";
import { isAttributeTemplate } from "./attributes";
import type {
  IDataObject,
  IIngredientRun,
  IIngredientSpec,
  IMaterialRun,
  IMaterialSpec,
  IMaterialTemplate,
  IMeasurementRun,
  IMeasurementSpec,
  IMeasurementTemplate,
  IObjectTemplate,
  IProcessRun,
  IProcessSpec,
  IProcessTemplate,
} from "./objects";
import {
  isIngredientRun,
  isIngredientSpec,
  isMaterialRun,
  isMaterialSpec,
  isMaterialTemplate,
  isMeasurementRun,
  isMeasurementSpec,
  isMeasurementTemplate,
  isObjectTemplate,
  isProcessRun,
  isProcessSpec,
  isProcessTemplate,
  isRunObject,
  isSpecObjectWithTemplate,
} from "./objects";
import { isLinkUid } from "./utils";

export * from "./attributes";
export * from "./objects";
export * from "./utils";

export type IDataTemplate = IObjectTemplate | IAttributeTemplate;
export type DataTemplateType = IDataTemplate["type"];
export type IDataObjectOrTemplate = IDataObject | IDataTemplate;
export type DataObjectOrTemplateType = IDataObjectOrTemplate["type"];
export type IDataConcept = IDataObjectOrTemplate | IScalarAttribute;
export type DataConceptType = IDataConcept["type"];

export const isDataTemplate = (
  obj?: ExplicitType<string>,
): obj is IDataTemplate => isObjectTemplate(obj) || isAttributeTemplate(obj);

/**
 * Ingredient Objects
 */
export type IIngredientObject = IIngredientRun | IIngredientSpec;
export const isIngredientObject = (
  obj?: IDataObjectOrTemplate,
): obj is IIngredientObject => isIngredientRun(obj) || isIngredientSpec(obj);

/**
 * Material Objects
 */
export type IMaterialObject = IMaterialRun | IMaterialSpec | IMaterialTemplate;
export const isMaterialObject = (
  obj?: IDataObjectOrTemplate,
): obj is IMaterialObject =>
  isMaterialRun(obj) || isMaterialSpec(obj) || isMaterialTemplate(obj);

/**
 * Measurement Objects
 */
export type IMeasurementObject =
  IMeasurementRun | IMeasurementSpec | IMeasurementTemplate;
export const isMeasurementObject = (
  obj?: IDataObjectOrTemplate,
): obj is IMeasurementObject =>
  isMeasurementRun(obj) || isMeasurementSpec(obj) || isMeasurementTemplate(obj);

/**
 * Process Objects
 */
export type IProcessObject = IProcessRun | IProcessSpec | IProcessTemplate;
export const isProcessObject = (
  obj?: IDataObjectOrTemplate,
): obj is IProcessObject =>
  isProcessRun(obj) || isProcessSpec(obj) || isProcessTemplate(obj);

/**
 * lookup utils
 */

// Since we use IDataObjectOrTemplate in a lot of places for convenience, this function gets the template id assuming its stored as link by id
export const getTemplateIdFromSpecObject = (
  specObject: IDataObjectOrTemplate | undefined,
) => {
  if (isSpecObjectWithTemplate(specObject)) {
    return isLinkUid(specObject.template) ? specObject.template.id : undefined;
  }
  return undefined;
};

// Since we use IDataObjectOrTemplate in a lot of places for convenience,
// this function gets the spec id either from link by id or the full spec object
export const getSpecIdFromDataObject = (
  dataObject: IDataObjectOrTemplate | undefined,
) => {
  if (isRunObject(dataObject)) {
    return isLinkUid(dataObject.spec)
      ? dataObject.spec.id
      : dataObject.spec.uids.id;
  }
  return undefined;
};

export function getDataObjectCategory(dataObject?: IDataObjectOrTemplate) {
  return isMaterialObject(dataObject)
    ? "material"
    : isMeasurementObject(dataObject)
      ? "measurement"
      : isProcessObject(dataObject)
        ? "process"
        : "ingredient";
}

export type DataObjectCategory = ReturnType<typeof getDataObjectCategory>;
